// Which extensions get a toolbar button, in what order, and how many fit. Pure functions over the
// settings (get = key -> value), shared by the toolbar (app/ext-bar.js), the extensions panel,
// Settings and the island.
//
// A page tool gets a button as soon as it is turned on ("auto"), unless it was taken off the toolbar
// (extUnpinned). The rest ("pin") only show once pinned by hand (extPinned). Turning an extension off
// hides its button but keeps the choice, so it comes back in the same place.
import { EXTENSIONS } from "./extensions.js";

// Default order for buttons nobody has moved.
export const TOOLBAR_ORDER = ["readaloud", "highlighter", "darkmode", "pip", "devtools", "adblock", "bandwidth", "bell", "music"];
const BY_ID = Object.fromEntries(EXTENSIONS.map((x) => [x.id, x]));
export const canPin = (id) => !!(BY_ID[id] && BY_ID[id].toolbar);

const list = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === "string" && canPin(x)) : []);

// Is this extension's button on the toolbar (when the extension is on)?
export function isPinned(id, get) {
	const x = BY_ID[id];
	if (!x || !x.toolbar) return false;
	if (list(get("extUnpinned")).includes(id)) return false;
	return x.toolbar === "auto" || list(get("extPinned")).includes(id);
}

// The buttons to show, in order: the hand-ordered ones first, then the default order.
export function toolbarExts(get) {
	const pinned = list(get("extPinned"));
	const order = [...new Set([...pinned, ...TOOLBAR_ORDER])];
	return order.filter((id) => get(BY_ID[id].key) === true && isPinned(id, get));
}

// How many buttons fit beside the address bar at this width; the rest go in the extensions menu.
export function maxButtons(width) {
	if (width >= 1200) return 5;
	if (width >= 900) return 3;
	if (width >= 600) return 2;
	return 0;
}
export function splitButtons(ids, width) {
	const n = maxButtons(width);
	return { shown: ids.slice(0, n), overflow: ids.slice(n) };
}

// The settings patch for each change. Arrays are always new, never edited in place.
export function pinPatch(id, on, get) {
	const pinned = list(get("extPinned")).filter((x) => x !== id);
	const unpinned = list(get("extUnpinned")).filter((x) => x !== id);
	if (on) {
		// Keep its place when it was already showing; otherwise it goes to the end.
		const shown = toolbarExts(get);
		const next = shown.includes(id) ? shown : [...shown, id];
		return { extPinned: [...new Set([...next, ...pinned])], extUnpinned: unpinned };
	}
	return { extPinned: pinned, extUnpinned: BY_ID[id] && BY_ID[id].toolbar === "auto" ? [...unpinned, id] : unpinned };
}
// Move a button one place left (-1) or right (+1). Every shown button is written into extPinned so
// the order sticks.
export function movePatch(id, dir, get) {
	const order = toolbarExts(get);
	const i = order.indexOf(id), j = i + dir;
	if (i < 0 || j < 0 || j >= order.length) return null;
	[order[i], order[j]] = [order[j], order[i]];
	const rest = list(get("extPinned")).filter((x) => !order.includes(x));
	return { extPinned: [...order, ...rest] };
}
